/**
 * Quote builder
 *
 * Corporate / white label products are quoted, not sold. This file holds the
 * whole client side of that: a persisted quote state, the volume discount and
 * branding maths, and the three custom elements that read and write it.
 *
 *   <quote-product-card>  one product: variant selection, quantity, add
 *   <quote-add-button>    the product page equivalent of a card
 *   <quote-summary>       the floating box: units, discount, branding, total
 *   <quote-form-bridge>   writes the quote into the contact form
 *
 * The quote lives in localStorage so it survives navigation between the
 * collection sections, the product pages and the form.
 *
 * Loaded from several sections, so every definition is guarded.
 */
(function () {
  'use strict';

  if (window.RowdyQuote) return;

  var STORAGE_KEY = 'rowdy-quote-v1';

  /* ------------------------------------------------------------------------
     Configuration
     ---------------------------------------------------------------------- */

  var defaults = {
    // Ordered, non-overlapping. A null rate means "we quote it by hand".
    tiers: [
      { min: 1, max: 4, rate: 0, hidden: true },
      { min: 5, max: 9, rate: 5 },
      { min: 10, max: 24, rate: 10 },
      { min: 25, max: 49, rate: 15 },
      { min: 50, max: 99, rate: 20 },
      { min: 100, max: 199, rate: 25 },
      { min: 200, max: null, rate: null }
    ],
    // Amounts are in cents of the shop currency, like every Shopify price.
    branding: [
      { id: 'none', label: 'Rowdy branding only', setup: 0, unit: 0 },
      { id: 'stamped', label: 'Your logo, stamped', setup: 7500, unit: 1500 },
      { id: 'engraved', label: 'Your logo, laser engraved', setup: 0, unit: 1000 }
    ],
    formUrl: '',
    quotedLabel: 'Quoted',
    strings: {}
  };

  var cfg = Object.assign({}, defaults, window.RowdyQuoteConfig || {});
  cfg.strings = Object.assign(
    {
      empty: 'Nothing in your quote yet. Pick a colour and a quantity to start.',
      heading: 'Your quote',
      units: 'units',
      unit: 'unit',
      subtotal: 'Subtotal',
      discount: 'Volume discount',
      saving: 'You save',
      branding: 'Branding',
      total: 'Estimated total',
      complete: 'Complete quote',
      clear: 'Clear quote',
      fine: 'Indicative only and excludes VAT and delivery. We confirm everything in your written quote.',
      capNote: 'Some items are capped at a lower discount.',
      nudge: 'Add {count} more {units} to reach {rate}% off.',
      quotedNudge: 'At 200+ units we price your quote by hand.',
      added: 'Added to your quote',
      unavailable: 'That combination is not available',
      remove: 'Remove',
      eachSuffix: 'each',
      decrease: 'Decrease quantity',
      increase: 'Increase quantity',
      quantity: 'Quantity',
      onceOff: 'once off',
      perUnit: 'per unit',
      setupFee: 'setup',
      totalUnitsLine: 'Total units',
      discountLine: 'Volume discount',
      brandingLine: 'Branding',
      totalLine: 'Estimated total',
      beforeQuoted: 'before quoted discount'
    },
    cfg.strings || {}
  );

  /* ------------------------------------------------------------------------
     Money
     ---------------------------------------------------------------------- */

  var decodedFormats = {};

  /**
   * Money formats are stored as HTML, so a euro sign can arrive as `&euro;`.
   * That is fine inside markup and wrong everywhere else we use it: the totals
   * are written with textContent, and the summary goes into a plain text email.
   */
  function decodeEntities(value) {
    if (value.indexOf('&') === -1) return value;
    if (typeof document === 'undefined' || !document.createElement) return value;

    var holder = document.createElement('textarea');
    holder.innerHTML = value;
    return holder.value;
  }

  function moneyFormat() {
    var raw =
      cfg.moneyFormat ||
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
      '{{amount}}';

    if (!(raw in decodedFormats)) decodedFormats[raw] = decodeEntities(raw);

    return decodedFormats[raw];
  }

  function group(number, thousands) {
    return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
  }

  /**
   * Shopify's money format strings, rendered client side.
   * @param {number} cents
   * @param {string} [format]
   * @returns {string}
   */
  function formatMoney(cents, format) {
    var value = Math.round(Number(cents) || 0);
    var pattern = format ? decodeEntities(format) : moneyFormat();
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = pattern.match(placeholder);
    if (!match) return pattern;

    var out;

    switch (match[1]) {
      case 'amount_no_decimals':
        out = group(String(Math.round(value / 100)), ',');
        break;
      case 'amount_with_comma_separator':
        out = group(String(Math.floor(value / 100)), '.') + ',' + ('0' + (Math.abs(value) % 100)).slice(-2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = group(String(Math.round(value / 100)), '.');
        break;
      case 'amount_with_apostrophe_separator':
        out = group(String(Math.floor(value / 100)), "'") + '.' + ('0' + (Math.abs(value) % 100)).slice(-2);
        break;
      case 'amount_with_space_separator':
        out = group(String(Math.floor(value / 100)), ' ') + ',' + ('0' + (Math.abs(value) % 100)).slice(-2);
        break;
      case 'amount_no_decimals_with_space_separator':
        out = group(String(Math.round(value / 100)), ' ');
        break;
      default:
        out = group(String(Math.floor(value / 100)), ',') + '.' + ('0' + (Math.abs(value) % 100)).slice(-2);
    }

    if (value < 0 && out.charAt(0) !== '-') out = '-' + out;

    return pattern.replace(placeholder, out);
  }

  /* ------------------------------------------------------------------------
     State
     ---------------------------------------------------------------------- */

  var state = { items: [], branding: cfg.branding[0].id };
  var listeners = [];

  function brandingOption(id) {
    for (var i = 0; i < cfg.branding.length; i++) {
      if (cfg.branding[i].id === id) return cfg.branding[i];
    }
    return cfg.branding[0];
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return;
      state.items = parsed.items.filter(function (item) {
        return item && item.key && Number(item.qty) > 0;
      });
      state.branding = brandingOption(parsed.branding).id;
    } catch (error) {
      // Private browsing, blocked storage, or a shape we no longer understand.
      // An empty quote is a fine place to start from.
    }
  }

  function write() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Nothing to do: the quote still works for this page view.
    }
  }

  function notify() {
    var totals = computeTotals();
    listeners.forEach(function (fn) {
      try {
        fn(state, totals);
      } catch (error) {
        console.error('[quote]', error);
      }
    });
    document.dispatchEvent(new CustomEvent('quote:change', { detail: { state: state, totals: totals } }));
  }

  function commit() {
    write();
    notify();
  }

  /* ------------------------------------------------------------------------
     Discount and branding maths
     ---------------------------------------------------------------------- */

  function tierFor(units) {
    for (var i = 0; i < cfg.tiers.length; i++) {
      var tier = cfg.tiers[i];
      if (units >= tier.min && (tier.max === null || units <= tier.max)) return tier;
    }
    return { min: 0, max: 0, rate: 0 };
  }

  function nextTierFrom(units) {
    for (var i = 0; i < cfg.tiers.length; i++) {
      if (cfg.tiers[i].min > units) return cfg.tiers[i];
    }
    return null;
  }

  /**
   * The band comes from the combined unit count across the whole quote, but
   * each line is then held to its own collection's ceiling. That is what lets
   * a Rowdy Business line sit at 15% inside a quote that has otherwise earned
   * 25%, without punishing the white label lines in the same basket.
   */
  function computeTotals() {
    var units = 0;
    var subtotal = 0;

    state.items.forEach(function (item) {
      units += Number(item.qty) || 0;
      subtotal += (Number(item.price) || 0) * (Number(item.qty) || 0);
    });

    var tier = tierFor(units);
    var quoted = tier.rate === null;
    var discount = 0;
    var capped = false;

    if (!quoted) {
      state.items.forEach(function (item) {
        var line = (Number(item.price) || 0) * (Number(item.qty) || 0);
        var cap = item.cap === null || item.cap === undefined ? 100 : Number(item.cap);
        var rate = Math.min(tier.rate, cap);
        if (rate < tier.rate) capped = true;
        discount += Math.round((line * rate) / 100);
      });
    }

    var branding = brandingOption(state.branding);
    var brandingCost = units > 0 ? branding.setup + branding.unit * units : 0;
    var total = subtotal - discount + brandingCost;
    var next = nextTierFrom(units);

    return {
      units: units,
      subtotal: subtotal,
      tier: tier,
      quoted: quoted,
      rate: quoted ? null : tier.rate,
      discount: discount,
      capped: capped,
      branding: branding,
      brandingCost: brandingCost,
      total: total,
      nextTier: next,
      unitsToNextTier: next ? next.min - units : 0
    };
  }

  /* ------------------------------------------------------------------------
     Plain text summary, used to fill the contact form message
     ---------------------------------------------------------------------- */

  function summaryLines() {
    var totals = computeTotals();
    var lines = [];

    state.items.forEach(function (item, index) {
      var line = index + 1 + '. ' + item.title;
      if (item.options) line += ' - ' + item.options;
      line +=
        ' - ' +
        item.qty +
        ' x ' +
        formatMoney(item.price) +
        ' = ' +
        formatMoney(item.price * item.qty);
      lines.push(line);
    });

    if (!lines.length) return [];

    lines.push('');
    lines.push(cfg.strings.totalUnitsLine + ': ' + totals.units);
    lines.push(
      cfg.strings.discountLine + ': ' +
        (totals.quoted ? cfg.quotedLabel : totals.rate + '%') +
        (totals.quoted ? '' : ' (' + formatMoney(totals.discount) + ')') +
        (totals.capped ? ' - ' + cfg.strings.capNote : '')
    );

    if (totals.branding.unit > 0 || totals.branding.setup > 0) {
      var brandingParts = [];
      if (totals.branding.setup > 0) {
        brandingParts.push(formatMoney(totals.branding.setup) + ' ' + cfg.strings.setupFee);
      }
      if (totals.branding.unit > 0) {
        brandingParts.push(formatMoney(totals.branding.unit) + ' x ' + totals.units);
      }
      lines.push(
        cfg.strings.brandingLine + ': ' +
          totals.branding.label +
          ' - ' +
          brandingParts.join(' + ') +
          ' = ' +
          formatMoney(totals.brandingCost)
      );
    } else {
      lines.push(cfg.strings.brandingLine + ': ' + totals.branding.label);
    }

    lines.push(
      cfg.strings.totalLine +
        ': ' +
        formatMoney(totals.total) +
        (totals.quoted ? ' (' + cfg.strings.beforeQuoted + ')' : '')
    );

    return lines;
  }

  /* ------------------------------------------------------------------------
     Public API
     ---------------------------------------------------------------------- */

  var RowdyQuote = {
    config: cfg,
    formatMoney: formatMoney,

    get items() {
      return state.items.slice();
    },

    get branding() {
      return state.branding;
    },

    totals: computeTotals,
    summaryLines: summaryLines,

    find: function (key) {
      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i].key === key) return state.items[i];
      }
      return null;
    },

    /**
     * @param {object} item - key, title, options, price (cents), image, url, cap, group
     * @param {number} qty - added to the existing quantity if the key is already there
     */
    add: function (item, qty) {
      var amount = Math.max(1, parseInt(qty, 10) || 1);
      var existing = this.find(item.key);

      if (existing) {
        existing.qty += amount;
        // A re-add refreshes the snapshot: prices and titles can move.
        existing.price = item.price;
        existing.title = item.title;
        existing.options = item.options;
        existing.image = item.image;
        existing.url = item.url;
        existing.cap = item.cap;
        existing.group = item.group;
      } else {
        state.items.push(Object.assign({}, item, { qty: amount }));
      }

      commit();
    },

    setQty: function (key, qty) {
      var amount = parseInt(qty, 10);
      if (isNaN(amount) || amount <= 0) return this.remove(key);

      var existing = this.find(key);
      if (!existing) return;
      existing.qty = amount;
      commit();
    },

    remove: function (key) {
      state.items = state.items.filter(function (item) {
        return item.key !== key;
      });
      commit();
    },

    clear: function () {
      state.items = [];
      commit();
    },

    setBranding: function (id) {
      state.branding = brandingOption(id).id;
      commit();
    },

    subscribe: function (fn) {
      listeners.push(fn);
      fn(state, computeTotals());
      return function () {
        listeners = listeners.filter(function (entry) {
          return entry !== fn;
        });
      };
    },

    /** Scroll to the form if it is on this page, otherwise navigate to it. */
    goToForm: function () {
      var target = document.getElementById('corp-quote');

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        var field = target.querySelector('input, select, textarea');
        if (field) {
          window.setTimeout(function () {
            field.focus({ preventScroll: true });
          }, 500);
        }
        return;
      }

      if (cfg.formUrl) {
        window.location.href = cfg.formUrl.indexOf('#') === -1 ? cfg.formUrl + '#corp-quote' : cfg.formUrl;
      }
    }
  };

  read();
  window.RowdyQuote = RowdyQuote;

  // Keep tabs in step with each other.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    read();
    notify();
  });

  // "Complete quote" appears in the floating box and on product pages, so it
  // is handled once here rather than by each element that renders one.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-quote-complete]');
    if (!button || button.disabled) return;
    event.preventDefault();
    RowdyQuote.goToForm();
  });

  /* ------------------------------------------------------------------------
     <quote-product-card>
     ---------------------------------------------------------------------- */

  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  if (!window.customElements.get('quote-product-card')) {
    window.customElements.define(
      'quote-product-card',
      class QuoteProductCard extends HTMLElement {
        connectedCallback() {
          var script = this.querySelector('script[data-quote-variants]');

          try {
            this.variants = JSON.parse(script ? script.textContent : '[]');
          } catch (error) {
            this.variants = [];
          }

          this.cap = this.hasAttribute('cap') ? Number(this.getAttribute('cap')) : 100;
          this.groupLabel = this.getAttribute('group-label') || '';
          this.productTitle = this.getAttribute('product-title') || '';
          this.url = this.getAttribute('product-url') || '';

          this.priceEl = this.querySelector('[data-quote-price]');
          this.imageEl = this.querySelector('[data-quote-image]');
          this.noteEl = this.querySelector('[data-quote-note]');
          this.qtyEl = this.querySelector('[data-quote-qty]');
          this.addEl = this.querySelector('[data-quote-add]');
          this.addLabel = this.addEl ? this.addEl.textContent : '';

          this.addEventListener('change', this.onChange.bind(this));
          this.addEventListener('click', this.onClick.bind(this));

          this.update();
        }

        onChange(event) {
          if (event.target.matches('input[type="radio"]') && event.target.closest('[data-quote-value]')) {
            this.update();
          }
        }

        onClick(event) {
          var step = event.target.closest('[data-quote-step]');

          if (step) {
            event.preventDefault();
            var current = parseInt(this.qtyEl.value, 10) || 1;
            var next = Math.max(1, current + Number(step.getAttribute('data-quote-step')));
            this.qtyEl.value = next;
            return;
          }

          if (event.target.closest('[data-quote-add]')) {
            event.preventDefault();
            this.addToQuote();
          }
        }

        /** The value of each option, in option position order. */
        selectedOptions() {
          var values = [];

          this.querySelectorAll('[data-quote-option-group]').forEach(function (fieldset) {
            var checked = fieldset.querySelector('input[type="radio"]:checked');
            var wrapper = checked ? checked.closest('[data-quote-value]') : null;
            values.push(wrapper ? wrapper.getAttribute('data-quote-value') : null);
          });

          return values;
        }

        matchedVariant() {
          var selected = this.selectedOptions();

          if (!selected.length) return this.variants[0] || null;
          if (selected.indexOf(null) !== -1) return null;

          for (var i = 0; i < this.variants.length; i++) {
            var variant = this.variants[i];
            var options = variant.options || [];
            var same = options.length === selected.length;

            for (var j = 0; same && j < options.length; j++) {
              if (normalise(options[j]) !== normalise(selected[j])) same = false;
            }

            if (same) return variant;
          }

          return null;
        }

        update() {
          var variant = this.matchedVariant();
          this.variant = variant;

          this.querySelectorAll('[data-quote-selected-value]').forEach(function (el) {
            var fieldset = el.closest('[data-quote-option-group]');
            var checked = fieldset ? fieldset.querySelector('input[type="radio"]:checked') : null;
            var wrapper = checked ? checked.closest('[data-quote-value]') : null;
            el.textContent = wrapper ? wrapper.getAttribute('data-quote-value') : '';
          });

          if (this.priceEl) {
            this.priceEl.textContent = variant ? formatMoney(variant.price) : '';
          }

          // Only touch the image when the variant actually points somewhere
          // else. Liquid already rendered the right one with a full srcset, and
          // clearing that on first paint costs every card its responsive
          // images: a phone would download the 800w file.
          if (this.imageEl && variant && variant.image && this.imageEl.getAttribute('src') !== variant.image) {
            this.imageEl.src = variant.image;
            this.imageEl.srcset = '';
          }

          if (this.addEl) {
            this.addEl.disabled = !variant;
            this.addEl.textContent = this.addLabel;
          }

          if (this.noteEl) {
            this.noteEl.textContent = variant ? '' : cfg.strings.unavailable;
          }
        }

        addToQuote() {
          var variant = this.variant;
          if (!variant) return;

          var qty = Math.max(1, parseInt(this.qtyEl ? this.qtyEl.value : 1, 10) || 1);

          RowdyQuote.add(
            {
              key: String(variant.id),
              id: variant.id,
              title: this.productTitle,
              options: (variant.options || []).join(' / '),
              price: variant.price,
              image: variant.image || this.getAttribute('product-image') || '',
              url: this.url,
              cap: this.cap,
              group: this.groupLabel
            },
            qty
          );

          if (this.addEl) {
            var label = this.addLabel;
            this.addEl.textContent = cfg.strings.added;
            window.clearTimeout(this.resetTimer);
            this.resetTimer = window.setTimeout(
              function () {
                this.addEl.textContent = label;
              }.bind(this),
              1600
            );
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <quote-add-button>

     The product page equivalent of a card. The theme's own variant picker
     already owns the selection there and re-renders this element whenever the
     variant changes, so the current variant is simply read off the attributes.
     ---------------------------------------------------------------------- */

  if (!window.customElements.get('quote-add-button')) {
    window.customElements.define(
      'quote-add-button',
      class QuoteAddButton extends HTMLElement {
        connectedCallback() {
          this.qtyEl = this.querySelector('[data-quote-qty]');
          this.addEl = this.querySelector('[data-quote-add]');
          this.addLabel = this.addEl ? this.addEl.textContent : '';
          this.addEventListener('click', this.onClick.bind(this));
        }

        onClick(event) {
          var step = event.target.closest('[data-quote-step]');

          if (step) {
            event.preventDefault();
            var current = parseInt(this.qtyEl.value, 10) || 1;
            this.qtyEl.value = Math.max(1, current + Number(step.getAttribute('data-quote-step')));
            return;
          }

          if (event.target.closest('[data-quote-add]')) {
            event.preventDefault();
            this.addToQuote();
          }
        }

        addToQuote() {
          var variantId = this.getAttribute('variant-id');
          if (!variantId) return;

          var qty = Math.max(1, parseInt(this.qtyEl ? this.qtyEl.value : 1, 10) || 1);

          RowdyQuote.add(
            {
              key: String(variantId),
              id: Number(variantId),
              title: this.getAttribute('product-title') || '',
              options: this.getAttribute('variant-options') || '',
              price: Number(this.getAttribute('price')) || 0,
              image: this.getAttribute('product-image') || '',
              url: this.getAttribute('product-url') || '',
              cap: Number(this.getAttribute('cap')),
              group: this.getAttribute('group-label') || ''
            },
            qty
          );

          if (this.addEl) {
            var label = this.addLabel;
            this.addEl.textContent = cfg.strings.added;
            window.clearTimeout(this.resetTimer);
            this.resetTimer = window.setTimeout(
              function () {
                this.addEl.textContent = label;
              }.bind(this),
              1600
            );
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <quote-summary>
     ---------------------------------------------------------------------- */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  if (!window.customElements.get('quote-summary')) {
    window.customElements.define(
      'quote-summary',
      class QuoteSummary extends HTMLElement {
        connectedCallback() {
          this.box = this.querySelector('.quote-summary');
          this.itemsEl = this.querySelector('[data-quote-items]');
          this.brandingEl = this.querySelector('[data-quote-branding]');
          this.hideWhenEmpty = this.hasAttribute('hide-when-empty');

          this.renderBranding();

          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('change', this.onChange.bind(this));
          this.addEventListener('input', this.onInput.bind(this));

          this.unsubscribe = RowdyQuote.subscribe(this.render.bind(this));
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        renderBranding() {
          if (!this.brandingEl) return;
          var name = 'quote-branding-' + Math.random().toString(36).slice(2, 8);

          this.brandingEl.innerHTML = cfg.branding
            .map(function (option) {
              var cost = [];
              if (option.setup > 0) cost.push(formatMoney(option.setup) + ' ' + cfg.strings.onceOff);
              if (option.unit > 0) cost.push(formatMoney(option.unit) + ' ' + cfg.strings.perUnit);

              return (
                '<label class="quote-branding__option">' +
                '<input type="radio" name="' +
                name +
                '" value="' +
                escapeHtml(option.id) +
                '" data-quote-branding-option>' +
                '<span>' +
                escapeHtml(option.label) +
                (cost.length ? '<span class="quote-branding__cost">' + escapeHtml(cost.join(' + ')) + '</span>' : '') +
                '</span>' +
                '</label>'
              );
            })
            .join('');
        }

        onClick(event) {
          var toggle = event.target.closest('[data-quote-toggle]');
          if (toggle) {
            this.box.classList.toggle('is-collapsed');
            toggle.setAttribute('aria-expanded', this.box.classList.contains('is-collapsed') ? 'false' : 'true');
            return;
          }

          var remove = event.target.closest('[data-quote-remove]');
          if (remove) {
            event.preventDefault();
            RowdyQuote.remove(remove.getAttribute('data-quote-remove'));
            return;
          }

          var step = event.target.closest('[data-quote-line-step]');
          if (step) {
            event.preventDefault();
            var key = step.getAttribute('data-quote-key');
            var item = RowdyQuote.find(key);
            if (item) RowdyQuote.setQty(key, item.qty + Number(step.getAttribute('data-quote-line-step')));
            return;
          }

          if (event.target.closest('[data-quote-clear]')) {
            event.preventDefault();
            RowdyQuote.clear();
          }
        }

        onChange(event) {
          if (event.target.matches('[data-quote-branding-option]')) {
            RowdyQuote.setBranding(event.target.value);
            return;
          }

          if (event.target.matches('[data-quote-line-qty]')) {
            RowdyQuote.setQty(event.target.getAttribute('data-quote-key'), event.target.value);
          }
        }

        onInput(event) {
          if (event.target.matches('[data-quote-line-qty]')) {
            var value = parseInt(event.target.value, 10);
            if (!isNaN(value) && value > 0) {
              RowdyQuote.setQty(event.target.getAttribute('data-quote-key'), value);
            }
          }
        }

        render(current, totals) {
          if (this.hideWhenEmpty && this.box) this.box.hidden = totals.units === 0;

          this.querySelectorAll('[data-quote-branding-option]').forEach(function (input) {
            input.checked = input.value === current.branding;
          });

          this.setText('[data-quote-count]', totals.units);
          this.setText('[data-quote-count-label]', totals.units === 1 ? cfg.strings.unit : cfg.strings.units);
          this.setText('[data-quote-subtotal]', formatMoney(totals.subtotal));
          this.setText('[data-quote-rate]', totals.quoted ? cfg.quotedLabel : totals.rate + '%');
          this.setText('[data-quote-saving]', totals.quoted ? cfg.quotedLabel : '- ' + formatMoney(totals.discount));
          this.setText('[data-quote-branding-label]', totals.branding.label);
          this.setText('[data-quote-branding-cost]', formatMoney(totals.brandingCost));
          this.setText('[data-quote-total]', formatMoney(totals.total));

          var capNote = this.querySelector('[data-quote-cap-note]');
          if (capNote) capNote.hidden = !totals.capped;

          var nudge = this.querySelector('[data-quote-nudge]');
          if (nudge) {
            if (totals.units === 0) {
              nudge.textContent = '';
            } else if (!totals.nextTier) {
              nudge.textContent = '';
            } else if (totals.nextTier.rate === null) {
              nudge.textContent = cfg.strings.quotedNudge;
            } else {
              nudge.textContent = cfg.strings.nudge
                .replace('{count}', totals.unitsToNextTier)
                .replace('{units}', totals.unitsToNextTier === 1 ? cfg.strings.unit : cfg.strings.units)
                .replace('{rate}', totals.nextTier.rate);
            }
          }

          this.renderItems(current.items);

          this.querySelectorAll('[data-quote-complete]').forEach(function (button) {
            button.disabled = current.items.length === 0;
          });
        }

        renderItems(items) {
          if (!this.itemsEl) return;

          if (!items.length) {
            this.itemsEl.innerHTML = '<p class="quote-summary__empty">' + escapeHtml(cfg.strings.empty) + '</p>';
            this.renderedKeys = '';
            return;
          }

          // While the same lines are on screen the markup is left alone and
          // only the numbers are written back, so typing into a quantity is
          // not interrupted by its own input being replaced.
          var keys = items
            .map(function (item) {
              return item.key;
            })
            .join(',');

          if (keys === this.renderedKeys) {
            var container = this.itemsEl;

            items.forEach(function (item) {
              var input = container.querySelector('[data-quote-line-qty][data-quote-key="' + item.key + '"]');
              if (input && input !== document.activeElement) input.value = item.qty;
            });

            return;
          }

          this.renderedKeys = keys;

          this.itemsEl.innerHTML = items
            .map(function (item) {
              var key = escapeHtml(item.key);

              return (
                '<div class="quote-line">' +
                (item.image
                  ? '<img class="quote-line__image" src="' +
                    escapeHtml(item.image) +
                    '" alt="" width="44" height="44" loading="lazy">'
                  : '<span class="quote-line__image"></span>') +
                '<span class="quote-line__info">' +
                '<a class="quote-line__title" href="' +
                escapeHtml(item.url || '#') +
                '">' +
                escapeHtml(item.title) +
                '</a>' +
                (item.options ? '<span class="quote-line__meta">' + escapeHtml(item.options) + '</span>' : '') +
                '<span class="quote-line__meta">' +
                escapeHtml(formatMoney(item.price)) +
                ' ' +
                escapeHtml(cfg.strings.eachSuffix) +
                '</span>' +
                '</span>' +
                '<span class="quote-line__side">' +
                '<span class="quote-qty">' +
                '<button type="button" class="quote-qty__button" data-quote-line-step="-1" data-quote-key="' +
                key +
                '" aria-label="' + escapeHtml(cfg.strings.decrease) + '">&minus;</button>' +
                '<input class="quote-qty__input" type="number" min="1" inputmode="numeric" value="' +
                escapeHtml(item.qty) +
                '" data-quote-line-qty data-quote-key="' +
                key +
                '" aria-label="' + escapeHtml(cfg.strings.quantity) + '">' +
                '<button type="button" class="quote-qty__button" data-quote-line-step="1" data-quote-key="' +
                key +
                '" aria-label="' + escapeHtml(cfg.strings.increase) + '">+</button>' +
                '</span>' +
                '<button type="button" class="quote-line__remove" data-quote-remove="' +
                key +
                '">' + escapeHtml(cfg.strings.remove) + '</button>' +
                '</span>' +
                '</div>'
              );
            })
            .join('');
        }

        setText(selector, value) {
          this.querySelectorAll(selector).forEach(function (el) {
            el.textContent = value;
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <quote-form-bridge>
     ---------------------------------------------------------------------- */

  var SENTINEL = '----- your notes below this line -----';

  if (!window.customElements.get('quote-form-bridge')) {
    window.customElements.define(
      'quote-form-bridge',
      class QuoteFormBridge extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;

          this.message = this.form.querySelector('textarea[name="contact[Message]"]');
          this.panel = this.querySelector('[data-quote-panel]');
          this.panelList = this.querySelector('[data-quote-panel-list]');
          this.panelTotal = this.querySelector('[data-quote-panel-total]');

          // Selects the quote can answer on the customer's behalf. Each stays
          // auto-filled until the customer touches it themselves.
          this.autoFields = ['contact[Route]', 'contact[Branding]'].reduce(
            function (map, name) {
              var field = this.form.querySelector('[name="' + name + '"]');
              if (field) {
                map[name] = field;
                field.dataset.quoteAuto = 'true';
                field.addEventListener('change', function () {
                  delete field.dataset.quoteAuto;
                });
              }
              return map;
            }.bind(this),
            {}
          );

          this.unsubscribe = RowdyQuote.subscribe(this.render.bind(this));
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        render(current, totals) {
          this.renderMessage();
          this.renderPanel(current.items, totals);
          this.renderAutoFields(current, totals);
        }

        /**
         * The message field belongs to the customer, so the quote is written
         * above a sentinel line and anything they typed below it is kept.
         */
        renderMessage() {
          if (!this.message) return;

          var lines = RowdyQuote.summaryLines();
          var existing = this.message.value;
          var index = existing.indexOf(SENTINEL);
          var notes = index === -1 ? existing.trim() : existing.slice(index + SENTINEL.length).replace(/^\n+/, '');

          if (!lines.length) {
            this.message.value = notes;
            return;
          }

          this.message.value = [cfg.strings.heading].concat(lines, ['', SENTINEL, '', notes]).join('\n');
        }

        renderPanel(items, totals) {
          if (!this.panel) return;
          this.panel.hidden = items.length === 0;
          if (!items.length) return;

          if (this.panelList) {
            this.panelList.innerHTML = items
              .map(function (item) {
                return (
                  '<li>' +
                  escapeHtml(item.qty) +
                  ' &times; ' +
                  escapeHtml(item.title) +
                  (item.options ? ' <span class="quote-line__meta">' + escapeHtml(item.options) + '</span>' : '') +
                  '</li>'
                );
              })
              .join('');
          }

          if (this.panelTotal) {
            this.panelTotal.innerHTML =
              '<span>' +
              escapeHtml(cfg.strings.total) +
              '</span><span>' +
              escapeHtml(formatMoney(totals.total)) +
              '</span>';
          }
        }

        renderAutoFields(current, totals) {
          if (!totals.units) return;

          var routeField = this.autoFields['contact[Route]'];
          if (routeField && routeField.dataset.quoteAuto) {
            var groups = {};
            current.items.forEach(function (item) {
              if (item.group) groups[item.group] = true;
            });
            var names = Object.keys(groups);
            if (names.length === 1) selectMatching(routeField, names[0]);
          }

          var brandingField = this.autoFields['contact[Branding]'];
          if (brandingField && brandingField.dataset.quoteAuto) {
            selectMatching(brandingField, totals.branding.label);
          }
        }
      }
    );
  }

  /** Picks the option whose text best matches, without clobbering a manual choice. */
  function selectMatching(select, text) {
    var wanted = normalise(text);
    if (!wanted) return;

    for (var i = 0; i < select.options.length; i++) {
      var option = select.options[i];
      var label = normalise(option.textContent);
      if (label === wanted || (label && label.indexOf(wanted) !== -1) || (wanted.indexOf(label) !== -1 && label)) {
        select.selectedIndex = i;
        return;
      }
    }
  }

  /* ------------------------------------------------------------------------
     Deep linking to a tab

     The quote collections section shows one collection per tab. A link of the
     form `/pages/business#quote-tab-2`, or `#quote-tab-<collection-handle>`,
     opens the section with that tab already selected.

     The tab strip is the theme's own `carousel-navigation`, which binds its
     click handlers when it upgrades, so this waits for the element to be
     defined and then clicks the button rather than reaching into the carousel.
     Clicking is also what keeps `aria-current` and the slide animation in step.
     ---------------------------------------------------------------------- */

  var TAB_PREFIX = 'quote-tab-';

  function requestedTab() {
    if (!window.location) return '';

    var hash = String(window.location.hash || '').replace(/^#/, '');

    try {
      hash = decodeURIComponent(hash);
    } catch (error) {
      // A malformed escape sequence is not a tab name; fall through with the raw hash.
    }

    return hash.indexOf(TAB_PREFIX) === 0 ? hash.slice(TAB_PREFIX.length) : '';
  }

  function tabButton(nav, wanted) {
    var buttons = Array.prototype.slice.call(nav.querySelectorAll('button[data-quote-tab]'));

    // A bare number is a position, counting the tabs as the customer sees them.
    if (/^[0-9]+$/.test(wanted)) return buttons[parseInt(wanted, 10) - 1] || null;

    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-quote-tab') === wanted) return buttons[i];
    }

    return null;
  }

  function openRequestedTab(scroll) {
    var wanted = requestedTab();
    if (!wanted) return;

    var elements = window.customElements;
    if (!elements || !elements.whenDefined) return;

    elements.whenDefined('carousel-navigation').then(function () {
      // One frame, so the carousel has laid out before it is asked to move.
      window.requestAnimationFrame(function () {
        var navs = document.querySelectorAll('[data-quote-tabs]');
        var found = null;
        var nav = null;

        for (var i = 0; i < navs.length && !found; i++) {
          found = tabButton(navs[i], wanted);
          nav = navs[i];
        }

        if (!found) return;

        if (found.getAttribute('aria-current') !== 'true') found.click();

        if (scroll) {
          var section = nav.closest('[data-quote-collections]') || nav;
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // Exposed so the behaviour is testable without a browser, and so a section
  // rendered after load can ask for the tab the URL named.
  RowdyQuote.requestedTab = requestedTab;
  RowdyQuote.openRequestedTab = openRequestedTab;

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        openRequestedTab(true);
      });
    } else {
      openRequestedTab(true);
    }
  }

  // A link to the same page only changes the hash, so no load event fires.
  window.addEventListener('hashchange', function () {
    openRequestedTab(true);
  });
})();
